<script lang="ts" setup>
    /**
     * The icon button of the chat dock: attach, send and stop in the input,
     * mute and hang up in a live call. One component, because the call bar
     * takes the place of the input and the two must not look like two
     * different pieces of software.
     *
     * Native attributes fall through to the button, so `title`, `disabled` and
     * `aria-pressed` are written by the caller as they would be on a `<button>`.
     */
    withDefaults(
        defineProps<{
            icon: string
            /** Written next to the icon, for an action nobody should have to guess from a glyph. */
            label?: string
            type?: 'button' | 'submit'
            /** `primary` is the filled circle of send and stop, the one action the dock is for. */
            modifiers?: 'danger' | 'primary'
        }>(),
        { label: undefined, type: 'button', modifiers: undefined },
    )
</script>

<template>
    <button
        class="pk-chat-button"
        :class="[
            modifiers ? `pk-chat-button--${modifiers}` : undefined,
            { 'pk-chat-button--labelled': label },
        ]"
        :type="type">
        <VvIcon :name="icon" class="pk-chat-button__icon" />
        <span v-if="label" class="pk-chat-button__label">{{ label }}</span>
    </button>
</template>

<style lang="scss">
    .pk-chat-button {
        position: relative;
        display: inline-flex;
        align-items: center;
        flex-shrink: 0;
        padding: var(--spacing-4);
        border: none;
        border-radius: var(--rounded);
        background-color: transparent;
        color: var(--color-word-1);
        cursor: pointer;
        transition: var(--transition-colors);

        &:hover {
            background-color: var(--color-surface-2);
        }

        // The fields' focus ring: a 1px surface-5 line measured 1.3:1 on the
        // surface, under the 3:1 a focus indicator needs
        &:focus-visible {
            outline: var(--spacing-2) solid var(--input-focus-color);
            outline-offset: 0;
        }

        &:disabled {
            cursor: not-allowed;
            opacity: 0.5;
        }

        // A finger needs 40px and the button is drawn at 28: on a touch
        // screen it answers 8px around itself, in every layout of the chat
        // (it was the fullscreen one only) and in the call bar, which mounts
        // the same button
        @media (pointer: coarse) {
            &::after {
                content: '';
                position: absolute;
                inset: calc(-1 * var(--spacing-8));
            }
        }

        // A toggle has to survive a glance: a crossed-out glyph alone changes
        // too few pixels to notice mid-sentence.
        &[aria-pressed='true'] {
            background-color: var(--color-surface-3);
            color: var(--color-word);
        }

        // A wash of the same red rather than a lighter red behind it: the
        // `lighten-5` step lands close enough to the icon to leave it at about
        // 2:1, which is a button that disappears exactly while it is being
        // pointed at. The tint composites over whatever surface is behind, so
        // it holds in the dark theme too.
        &--danger {
            // The project's text shade of danger; the fill where a host app
            // loads only the library and this package's stylesheet
            color: var(--u-danger-text, var(--color-danger));

            &:hover {
                background-color: color-mix(
                    in srgb,
                    var(--color-danger) 12%,
                    transparent
                );
            }
        }

        // Same 28px box as the ghost buttons, so the dock keeps its height,
        // with a smaller glyph because a filled shape needs air around it.
        // Its edge is visible, so it sits on the input's padding line just as
        // the text does: a ghost glyph sat 6px further in and made the two
        // sides of the input look unequal. Empty, it turns into a grey pad
        // with the fields' disabled ink: a light arrow on that pad vanished,
        // and with it the circle's edge, which is what squares the sides.
        // The agent's color when the widget has one, like the user's bubble.
        &--primary {
            --pk-chat-button-fill: var(
                --chatbot-main-color,
                var(--color-word-1)
            );

            padding: var(--spacing-6);
            border-radius: var(--rounded-full);
            background-color: var(--pk-chat-button-fill);
            color: var(--chatbot-contrast-color, var(--color-surface));

            &:hover {
                background-color: color-mix(
                    in srgb,
                    var(--pk-chat-button-fill) 85%,
                    var(--color-surface)
                );
            }

            // Off the disc, or a dark ring would sink into a dark fill
            &:focus-visible {
                outline-offset: var(--spacing-2);
            }

            &:disabled {
                opacity: 1;
                background-color: var(--color-surface-3);
                color: var(--color-word-3);
            }

            .pk-chat-button__icon {
                width: var(--spacing-16);
                height: var(--spacing-16);
            }
        }

        &--labelled {
            gap: var(--spacing-4);
            padding-inline: var(--spacing-8);
            font-size: var(--text-12);
        }

        &__icon {
            display: block;
            width: var(--spacing-20);
            height: var(--spacing-20);
        }
    }
</style>
